import React, { Suspense, lazy, useEffect, useState } from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ThemeProvider } from "./context/ThemeContext";
import { SiteNameProvider } from "./context/SiteNameContext";
import api from "./api";
import { getStoredTenantSlug, getTenantHomePath, getTenantLoginPath, getTenantScopedPath } from "./utils/tenantRoutes";
import { hasApprovedSiteAccess } from "./utils/access";
import LoadingScreen from "./components/LoadingScreen";

const HomePublic = lazy(() => import("./components/HomePublic"));
const AlumniRegister = lazy(() => import("./components/AlumniRegister"));
const GuestRegister = lazy(() => import("./components/GuestRegister"));
const CompanyRegister = lazy(() => import("./components/CompanyRegister"));
const Login = lazy(() => import("./components/Login"));
const ForgotPassword = lazy(() => import("./components/ForgotPassword"));
const ResetPassword = lazy(() => import("./components/ResetPassword"));
const AboutPublic = lazy(() => import("./components/AboutPublic"));
const ContactPublic = lazy(() => import("./components/ContactPublic"));

const HomeDashboard = lazy(() => import("./main_components/HomeDashboard"));
const UserProfile = lazy(() => import("./main_components/UserProfile"));
const OrganizationDashboard = lazy(() => import("./main_components/OrganizationDashboard"));
const ContributorsDashboard = lazy(() => import("./main_components/ContributorsDashboard"));
const MentorshipDashboard = lazy(() => import("./main_components/MentorshipDashboard"));
const MentorshipGuidancePage = lazy(() => import("./main_components/MentorshipGuidancePage"));
const CareersDashboard = lazy(() => import("./main_components/CareersDashboard"));
const CareersGuidancePage = lazy(() => import("./main_components/CareersGuidancePage"));
const NewsDashboard = lazy(() => import("./main_components/NewsDashboard"));
const DonationDashboard = lazy(() => import("./main_components/DonationDashboard"));
const EventsDashboard = lazy(() => import("./main_components/EventsDashboard"));
const ContactDashboard = lazy(() => import("./main_components/ContactDashboard"));
const AboutDashboard = lazy(() => import("./main_components/AboutDashboard"));

const AdminLogin = lazy(() => import("./admin_components/AdminLogin"));
const AdminDashboard = lazy(() => import("./admin_components/AdminDashboard"));
const PagesList = lazy(() => import("./admin_components/PagesList"));
const HomePageEditor = lazy(() => import("./admin_components/HomePageEditor"));
const CommitteeAdmin = lazy(() => import("./admin_components/CommitteeAdmin"));
const NewsAdmin = lazy(() => import("./admin_components/NewsAdmin"));
const CareersAdmin = lazy(() => import("./admin_components/CareersAdmin"));
const EventsAdmin = lazy(() => import("./admin_components/EventsAdmin"));
const MentorshipAdmin = lazy(() => import("./admin_components/MentorshipAdmin"));
const ContactAdmin = lazy(() => import("./admin_components/ContactAdmin"));
const UserManagement = lazy(() => import("./admin_components/UserManagement"));
const CompanyManagement = lazy(() => import("./admin_components/CompanyManagement"));
const FooterAdmin = lazy(() => import("./admin_components/FooterAdmin"));
const Theme = lazy(() => import("./admin_components/Theme"));
const AboutAdmin = lazy(() => import("./admin_components/AboutAdmin"));
const DonationAdmin = lazy(() => import("./admin_components/DonationAdmin"));
const SupportersAdmin = lazy(() => import("./admin_components/SupportersAdmin"));
const ContributorsAdmin = lazy(() => import("./admin_components/ContributorsAdmin"));
const InquiryComplaintsAdmin = lazy(() => import("./admin_components/InquiryComplaintsAdmin"));
const GeneralSettings = lazy(() => import("./admin_components/GeneralSettings"));
const AdminHome = lazy(() => import("./admin_components/AdminHome"));

   
function getTenantAdminPath(path = "") {
  const tenantSlug = localStorage.getItem("tenant_slug");
  if (!tenantSlug) return "/admin/login";
  return `/${tenantSlug}/admin${path}`;
}

const RESERVED_ROOT_SEGMENTS = new Set([
  "admin",
  "home-public",
  "about",
  "contact",
  "register",
  "alumni-register",
  "member-register",
  "guest-register",
  "company-register",
  "login",
  "forgot-password",
  "reset-password",
  "dashboard",
  "profile",
  "contact-dashboard",
  "organization",
  "mentorship",
  "mentors-guidance",
  "mentees-guidance",
  "careers",
  "employer-guidance",
  "applicants-guidance",
  "news",
  "donate",
  "events",
  "alumnione",
]);

function getRouteTenantSlug() {
  const path = window.location.pathname || "";
  const segments = path.split("/").filter(Boolean);
  if (!segments.length) return "";

  const first = String(segments[0]).trim().toLowerCase();
  if (!first || RESERVED_ROOT_SEGMENTS.has(first)) return "";

  return first;
}

const ALUMNIONE_TENANT_SLUG = "mahapola-alumni";
const tenantResponseCache = new Map();
const tenantRequestCache = new Map();

function getLocalTenantForSlug(slug) {
  const normalizedSlug = String(slug || "").trim().toLowerCase();
  const storedSlug = String(localStorage.getItem("tenant_slug") || "").trim().toLowerCase();
  const storedId = localStorage.getItem("tenant_id");

  if (normalizedSlug && storedSlug === normalizedSlug && storedId) {
    return { id: Number(storedId), slug: storedSlug };
  }

  return null;
}

async function resolveTenantBySlug(slug) {
  const normalizedSlug = String(slug || "").trim().toLowerCase();
  if (!normalizedSlug) return null;

  const localTenant = getLocalTenantForSlug(normalizedSlug);
  if (localTenant) {
    tenantResponseCache.set(normalizedSlug, localTenant);
    return localTenant;
  }

  const cachedTenant = tenantResponseCache.get(normalizedSlug);
  if (cachedTenant?.id) {
    return cachedTenant;
  }

  const pending = tenantRequestCache.get(normalizedSlug);
  if (pending) {
    return pending;
  }

  const request = api
    .get(`/tenant/slug/${encodeURIComponent(normalizedSlug)}`)
    .then((res) => {
      const tenant = res.data?.tenant || res.data;
      if (tenant?.id) {
        const resolved = {
          ...tenant,
          slug: String(tenant.slug || normalizedSlug).toLowerCase(),
        };
        localStorage.setItem("tenant_id", String(resolved.id));
        localStorage.setItem("tenant_slug", String(resolved.slug));
        tenantResponseCache.set(normalizedSlug, resolved);
        return resolved;
      }

      return null;
    })
    .catch(() => null)
    .finally(() => {
      tenantRequestCache.delete(normalizedSlug);
    });

  tenantRequestCache.set(normalizedSlug, request);
  return request;
}

function AlumniOneAdminAliasRoute({ user }) {
  useEffect(() => {
    // Prime demo tenant context when available, without blocking the public SaaS landing page.
    resolveTenantBySlug(ALUMNIONE_TENANT_SLUG);
  }, []);

  return <AdminHome user={user} />;
}

function TenantHomeRoute() {
  const { tenantSlug } = useParams();
  const [loadingTenant, setLoadingTenant] = useState(true);
  const [isValidTenant, setIsValidTenant] = useState(false);

  useEffect(() => {
    let mounted = true;

    const resolveTenant = async () => {
      try {
        const slug = String(tenantSlug || "").trim().toLowerCase();
        if (!slug) {
          if (mounted) {
            setIsValidTenant(false);
            setLoadingTenant(false);
          }
          return;
        }

        const tenant = await resolveTenantBySlug(slug);
        if (mounted) {
          setIsValidTenant(Boolean(tenant?.id));
        }
      } catch {
        if (mounted) setIsValidTenant(false);
      } finally {
        if (mounted) {
          setLoadingTenant(false);
        }
      }
    };

    setLoadingTenant(true);
    setIsValidTenant(false);
    resolveTenant();

    return () => {
      mounted = false;
    };
  }, [tenantSlug]);

  if (loadingTenant) return <LoadingScreen />;
  if (!isValidTenant) return <Navigate to="/home-public" replace />;

  return <HomePublic />;
}

function TenantSlugRedirect({ path }) {
  const { tenantSlug } = useParams();
  return <Navigate to={getTenantScopedPath(path, tenantSlug)} replace />;
}

/* Protect normal logged-in routes */
function PrivateRoute({ user, children }) {
  const hasToken = Boolean(localStorage.getItem("token"));
  if (!user && hasToken) return children;
  if (!user) return <Navigate to={getTenantLoginPath()} replace />;
  return children;
}

function MemberRoute({ user, allowNonMember = false, children }) {
  const hasToken = Boolean(localStorage.getItem("token"));
  if (!user && hasToken) return children;
  if (!user) return <Navigate to={getTenantLoginPath()} replace />;
  if (user?.role === "admin" || hasApprovedSiteAccess(user) || allowNonMember) return children;
  return <Navigate to={getTenantScopedPath("/alumni-register")} replace />;
}

function TenantRoute({ user, adminOnly = false, children, fallback = "/home-public" }) {
  const { tenantSlug } = useParams();
  const [loadingTenant, setLoadingTenant] = useState(true);
  const [resolvedTenant, setResolvedTenant] = useState(null);

  useEffect(() => {
    let mounted = true;

    const syncTenant = async () => {
      const routeSlug = String(tenantSlug || getRouteTenantSlug() || "").trim().toLowerCase();
      if (!routeSlug) {
        if (mounted) {
          setResolvedTenant(null);
          setLoadingTenant(false);
        }
        return;
      }

      try {
        const tenant = await resolveTenantBySlug(routeSlug);
        if (mounted) setResolvedTenant(tenant?.id ? tenant : null);
      } catch {
        if (mounted) {
          setResolvedTenant(null);
        }
      } finally {
        if (mounted) {
          setLoadingTenant(false);
        }
      }
    };

    setLoadingTenant(true);
    setResolvedTenant(null);
    syncTenant();

    return () => {
      mounted = false;
    };
  }, [tenantSlug]);

  if (loadingTenant) return <LoadingScreen />;
  if (!resolvedTenant) return <Navigate to={fallback} replace />;

  if (adminOnly && (!user || user.role !== "admin")) {
    return <Navigate to={`/${resolvedTenant.slug}/admin/login`} replace />;
  }

  return typeof children === "function" ? children(resolvedTenant) : children;
}

function getStoredUser() {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function App() {
  const token = localStorage.getItem("token");
  const [user, setUser] = useState(() => getStoredUser());
  const [loading, setLoading] = useState(() => {
    const hasToken = Boolean(localStorage.getItem("token"));
    return hasToken && !getStoredUser();
  });

  useEffect(() => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    let mounted = true;
    const cachedUser = getStoredUser();

    if (cachedUser) {
      setUser(cachedUser);
      setLoading(false);
    }

    const bootstrapTenantAndLoadUser = async () => {
      try {
        const routeSlug = getRouteTenantSlug();
        const tenantPromise = routeSlug
          ? resolveTenantBySlug(routeSlug)
          : Promise.resolve(null);

        const [res] = await Promise.all([api.get("/me"), tenantPromise]);
        if (!mounted) return;

        const resolvedUser = res.data?.user || res.data || null;
        setUser(resolvedUser);
        if (resolvedUser) {
          localStorage.setItem("user", JSON.stringify(resolvedUser));
        }
        setLoading(false);
      } catch {
        if (!mounted) return;

        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setUser(null);
        setLoading(false);
      }
    };

    bootstrapTenantAndLoadUser();

    return () => {
      mounted = false;
    };
  }, [token]);

  if (loading && !token) return <LoadingScreen />;

  return (
    <GoogleOAuthProvider clientId={String(process.env.REACT_APP_GOOGLE_CLIENT_ID || "").trim()}>
      <SiteNameProvider>
        <ThemeProvider>
          <Suspense fallback={<LoadingScreen />}>
          <Routes>
      {/* Public Routes */}
      <Route 
        path="/" 
        element={
          user ? (
            <PrivateRoute user={user}>
              <MemberRoute user={user} allowNonMember>
                <HomeDashboard user={user} />
              </MemberRoute>
            </PrivateRoute>
          ) : (
            <Navigate to={getTenantHomePath()} replace />
          )
        } 
      />
      <Route
        path="/admin"
        element={
          user?.role === "admin" ? (
            <Navigate to={getTenantAdminPath("")} replace />
          ) : (
            <Navigate to={getTenantAdminPath("")} replace />
          )
        }
      />
      <Route
        path="/admin/home"
        element={
          user?.role === "admin" ? (
            <Navigate to={getTenantAdminPath("")} replace />
          ) : (
            <Navigate to={getTenantAdminPath("")} replace />
          )
        }
      />
      <Route
        path="/home-public"
        element={
          (() => {
            const storedSlug = getStoredTenantSlug() || ALUMNIONE_TENANT_SLUG;
            return <Navigate to={getTenantHomePath(storedSlug)} replace />;
          })()
        }
      />
      <Route
        path="/:tenantSlug"
        element={
          user ? <TenantSlugRedirect path="/dashboard" /> : <TenantHomeRoute />
        }
      />
      <Route
        path="/:tenantSlug/home"
        element={user ? <TenantSlugRedirect path="/dashboard" /> : <TenantHomeRoute />}
      />
      <Route path="/about" element={<AboutPublic />} />
      <Route
        path="/:tenantSlug/about"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <AboutPublic />
          </TenantRoute>
        }
      />
      <Route path="/contact" element={<ContactPublic />} />
      <Route
        path="/:tenantSlug/contact"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <ContactPublic />
          </TenantRoute>
        }
      />
      <Route path="/register" element={<Navigate to={getTenantLoginPath()} replace />} />
      <Route path="/:tenantSlug/register" element={<TenantSlugRedirect path="/login" />} />
      <Route
        path="/alumni-register"
        element={
          <PrivateRoute user={user}>
            <AlumniRegister />
          </PrivateRoute>
        }
      />
      <Route
        path="/:tenantSlug/alumni-register"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <PrivateRoute user={user}>
              <AlumniRegister />
            </PrivateRoute>
          </TenantRoute>
        }
      />
      <Route
        path="/member-register"
        element={
          <PrivateRoute user={user}>
            <GuestRegister />
          </PrivateRoute>
        }
      />
      <Route
        path="/:tenantSlug/member-register"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <PrivateRoute user={user}>
              <GuestRegister />
            </PrivateRoute>
          </TenantRoute>
        }
      />
      <Route
        path="/guest-register"
        element={
          <PrivateRoute user={user}>
            <GuestRegister />
          </PrivateRoute>
        }
      />
      <Route
        path="/:tenantSlug/guest-register"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <PrivateRoute user={user}>
              <GuestRegister />
            </PrivateRoute>
          </TenantRoute>
        }
      />
      <Route
        path="/company-register"
        element={
          <PrivateRoute user={user}>
            <CompanyRegister />
          </PrivateRoute>
        }
      />
      <Route
        path="/:tenantSlug/company-register"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <PrivateRoute user={user}>
              <CompanyRegister />
            </PrivateRoute>
          </TenantRoute>
        }
      />

      <Route
        path="/login"
        element={
          <Login
            onLogin={(loggedUser) => {
              setUser(loggedUser);
              const tenantSlug = localStorage.getItem("tenant_slug");
              if (loggedUser?.role === "admin") {
                window.location.href = tenantSlug ? `/${tenantSlug}/admin/dashboard` : "/admin/dashboard";
              } else {
                window.location.href = getTenantScopedPath("/dashboard", tenantSlug || undefined);
              }
            }}
          />
        }
      />

      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route
        path="/:tenantSlug/forgot-password"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <ForgotPassword />
          </TenantRoute>
        }
      />

      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/:tenantSlug/reset-password"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <ResetPassword />
          </TenantRoute>
        }
      />

      <Route
        path="/:tenantSlug/login"
        element={
          <TenantRoute user={user} fallback="/home-public">
            {user ? (
              <TenantSlugRedirect path="/dashboard" />
            ) : (
              <Login
                onLogin={(loggedUser) => {
                  setUser(loggedUser);
                  const tenantSlug = localStorage.getItem("tenant_slug");
                  if (loggedUser?.role === "admin") {
                    window.location.href = tenantSlug ? `/${tenantSlug}/admin/dashboard` : "/admin/dashboard";
                  } else {
                    window.location.href = getTenantScopedPath("/dashboard", tenantSlug || undefined);
                  }
                }}
              />
            )}
          </TenantRoute>
        }
      />

      <Route
        path="/admin/login"
        element={
          user?.role === "admin" ? (
            <Navigate to={getTenantAdminPath("/dashboard")} replace />
          ) : (
            <AdminLogin
              onLogin={(loggedUser) => {
                setUser(loggedUser);
              }}
            />
          )
        }
      />

      {/* Normal Auth Routes */}
      <Route
        path="/dashboard"
        element={
          <PrivateRoute user={user}>
            <MemberRoute user={user} allowNonMember>
              <HomeDashboard user={user} />
            </MemberRoute>
          </PrivateRoute>
        }
      />

      <Route
        path="/:tenantSlug/dashboard"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <PrivateRoute user={user}>
              <MemberRoute user={user} allowNonMember>
                <HomeDashboard user={user} />
              </MemberRoute>
            </PrivateRoute>
          </TenantRoute>
        }
      />

      <Route
        path="/profile"
        element={
          <PrivateRoute user={user}>
            <MemberRoute user={user}>
              <UserProfile user={user} />
            </MemberRoute>
          </PrivateRoute>
        }
      />

      <Route
        path="/:tenantSlug/profile"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <PrivateRoute user={user}>
              <MemberRoute user={user}>
                <UserProfile user={user} />
              </MemberRoute>
            </PrivateRoute>
          </TenantRoute>
        }
      />

      <Route
        path="/contact-dashboard"
        element={
          <PrivateRoute user={user}>
            <MemberRoute user={user}>
              <ContactDashboard user={user} />
            </MemberRoute>
          </PrivateRoute>
        }
      />

      <Route
        path="/organization"
        element={
          <PrivateRoute user={user}>
            <MemberRoute user={user}>
              <OrganizationDashboard user={user} />
            </MemberRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/:tenantSlug/organization"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <PrivateRoute user={user}>
              <MemberRoute user={user}>
                <OrganizationDashboard user={user} />
              </MemberRoute>
            </PrivateRoute>
          </TenantRoute>
        }
      />
      <Route
        path="/mentorship"
        element={<MentorshipDashboard user={user} />}
      />
      <Route
        path="/:tenantSlug/mentorship"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <MentorshipDashboard user={user} />
          </TenantRoute>
        }
      />
      <Route
        path="/mentors-guidance"
        element={<MentorshipGuidancePage user={user} type="mentors" />}
      />
      <Route
        path="/:tenantSlug/mentors-guidance"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <MentorshipGuidancePage user={user} type="mentors" />
          </TenantRoute>
        }
      />
      <Route
        path="/mentees-guidance"
        element={<MentorshipGuidancePage user={user} type="mentees" />}
      />
      <Route
        path="/:tenantSlug/mentees-guidance"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <MentorshipGuidancePage user={user} type="mentees" />
          </TenantRoute>
        }
      />

      <Route
        path="/careers"
        element={<CareersDashboard user={user} />}
      />
      <Route
        path="/:tenantSlug/careers"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <CareersDashboard user={user} />
          </TenantRoute>
        }
      />
      <Route
        path="/employer-guidance"
        element={<CareersGuidancePage user={user} type="employer" />}
      />
      <Route
        path="/:tenantSlug/employer-guidance"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <CareersGuidancePage user={user} type="employer" />
          </TenantRoute>
        }
      />
      <Route
        path="/applicants-guidance"
        element={<CareersGuidancePage user={user} type="applicant" />}
      />
      <Route
        path="/:tenantSlug/applicants-guidance"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <CareersGuidancePage user={user} type="applicant" />
          </TenantRoute>
        }
      />

      <Route
        path="/news"
        element={
          <PrivateRoute user={user}>
            <MemberRoute user={user}>
              <NewsDashboard user={user} />
            </MemberRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/:tenantSlug/news"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <PrivateRoute user={user}>
              <MemberRoute user={user}>
                <NewsDashboard user={user} />
              </MemberRoute>
            </PrivateRoute>
          </TenantRoute>
        }
      />

      <Route
        path="/donate"
        element={<DonationDashboard user={user} />}
      />
      <Route
        path="/:tenantSlug/donate"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <DonationDashboard user={user} />
          </TenantRoute>
        }
      />

      <Route
        path="/events"
        element={
          <PrivateRoute user={user}>
            <MemberRoute user={user}>
              <EventsDashboard user={user} />
            </MemberRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/:tenantSlug/events"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <PrivateRoute user={user}>
              <MemberRoute user={user}>
                <EventsDashboard user={user} />
              </MemberRoute>
            </PrivateRoute>
          </TenantRoute>
        }
      />

      <Route
        path="/contributors"
        element={
          <PrivateRoute user={user}>
            <MemberRoute user={user}>
              <ContributorsDashboard user={user} />
            </MemberRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/:tenantSlug/contributors"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <PrivateRoute user={user}>
              <MemberRoute user={user}>
                <ContributorsDashboard user={user} />
              </MemberRoute>
            </PrivateRoute>
          </TenantRoute>
        }
      />

      <Route
        path="/dashboard/about"
        element={
          <PrivateRoute user={user}>
            <MemberRoute user={user}>
              <AboutDashboard user={user} />
            </MemberRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/:tenantSlug/dashboard/about"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <PrivateRoute user={user}>
              <MemberRoute user={user}>
                <AboutDashboard user={user} />
              </MemberRoute>
            </PrivateRoute>
          </TenantRoute>
        }
      />

      <Route
        path="/dashboard/contact"
        element={
          <PrivateRoute user={user}>
            <MemberRoute user={user}>
              <ContactDashboard user={user} />
            </MemberRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/:tenantSlug/dashboard/contact"
        element={
          <TenantRoute user={user} fallback="/home-public">
            <PrivateRoute user={user}>
              <MemberRoute user={user}>
                <ContactDashboard user={user} />
              </MemberRoute>
            </PrivateRoute>
          </TenantRoute>
        }
      />

      {/* ADMIN ROUTES - TENANT SCOPED */}
      <Route
        path="/:tenantSlug/admin/login"
        element={
          <TenantRoute user={user} fallback="/admin/login">
            {(tenant) =>
              user?.role === "admin" ? (
                <Navigate to={`/${tenant.slug}/admin/dashboard`} replace />
              ) : (
                <AdminLogin
                  tenantSlug={tenant.slug}
                  onLogin={(loggedUser) => {
                    setUser(loggedUser);
                  }}
                />
              )
            }
          </TenantRoute>
        }
      />

      <Route
        path="/:tenantSlug/admin"
        element={<Navigate to="/alumnione" replace />}
      />

      <Route path="/alumnione" element={<AlumniOneAdminAliasRoute user={user} />} />

      <Route
        path="/:tenantSlug/admin/home"
        element={<Navigate to="/alumnione" replace />}
      />

      <Route
        path="/:tenantSlug/admin/dashboard"
        element={
          <TenantRoute user={user} adminOnly fallback="/admin/login">
            <AdminDashboard />
          </TenantRoute>
        }
      />

      <Route
        path="/:tenantSlug/admin/pages"
        element={
          <TenantRoute user={user} adminOnly fallback="/admin/login">
            <PagesList />
          </TenantRoute>
        }
      />

      <Route
        path="/:tenantSlug/admin/theme"
        element={
          <TenantRoute user={user} adminOnly fallback="/admin/login">
            <Theme />
          </TenantRoute>
        }
      />

      <Route
        path="/:tenantSlug/admin/home-editor"
        element={
          <TenantRoute user={user} adminOnly fallback="/admin/login">
            <HomePageEditor />
          </TenantRoute>
        }
      />

      <Route
        path="/:tenantSlug/admin/committee"
        element={
          <TenantRoute user={user} adminOnly fallback="/admin/login">
            <CommitteeAdmin />
          </TenantRoute>
        }
      />

      <Route
        path="/:tenantSlug/admin/news"
        element={
          <TenantRoute user={user} adminOnly fallback="/admin/login">
            <NewsAdmin />
          </TenantRoute>
        }
      />

      <Route
        path="/:tenantSlug/admin/careers"
        element={
          <TenantRoute user={user} adminOnly fallback="/admin/login">
            <CareersAdmin />
          </TenantRoute>
        }
      />

      <Route
        path="/:tenantSlug/admin/mentorship"
        element={
          <TenantRoute user={user} adminOnly fallback="/admin/login">
            <MentorshipAdmin />
          </TenantRoute>
        }
      />

      <Route
        path="/:tenantSlug/admin/events"
        element={
          <TenantRoute user={user} adminOnly fallback="/admin/login">
            <EventsAdmin />
          </TenantRoute>
        }
      />

      <Route
        path="/:tenantSlug/admin/contact"
        element={
          <TenantRoute user={user} adminOnly fallback="/admin/login">
            <ContactAdmin />
          </TenantRoute>
        }
      />

      <Route
        path="/:tenantSlug/admin/user-management"
        element={
          <TenantRoute user={user} adminOnly fallback="/admin/login">
            <UserManagement />
          </TenantRoute>
        }
      />

      <Route
        path="/:tenantSlug/admin/company-management"
        element={
          <TenantRoute user={user} adminOnly fallback="/admin/login">
            <CompanyManagement />
          </TenantRoute>
        }
      />

      <Route
        path="/:tenantSlug/admin/general-settings"
        element={
          <TenantRoute user={user} adminOnly fallback="/admin/login">
            <GeneralSettings />
          </TenantRoute>
        }
      />

      <Route
        path="/:tenantSlug/admin/footer"
        element={
          <TenantRoute user={user} adminOnly fallback="/admin/login">
            <FooterAdmin />
          </TenantRoute>
        }
      />

      <Route
        path="/:tenantSlug/admin/about"
        element={
          <TenantRoute user={user} adminOnly fallback="/admin/login">
            <AboutAdmin />
          </TenantRoute>
        }
      />

      <Route
        path="/:tenantSlug/admin/donation"
        element={
          <TenantRoute user={user} adminOnly fallback="/admin/login">
            <DonationAdmin />
          </TenantRoute>
        }
      />

      <Route
        path="/:tenantSlug/admin/supporters"
        element={
          <TenantRoute user={user} adminOnly fallback="/admin/login">
            <SupportersAdmin />
          </TenantRoute>
        }
      />

      <Route
        path="/:tenantSlug/admin/contributors"
        element={
          <TenantRoute user={user} adminOnly fallback="/admin/login">
            <ContributorsAdmin />
          </TenantRoute>
        }
      />

      <Route
        path="/:tenantSlug/admin/inquiries"
        element={
          <TenantRoute user={user} adminOnly fallback="/admin/login">
            <InquiryComplaintsAdmin />
          </TenantRoute>
        }
      />

      <Route
        path="/admin"
        element={
          user?.role === "admin" ? (
            <Navigate to={getTenantAdminPath("/dashboard")} replace />
          ) : (
            <Navigate to={getTenantAdminPath("/login")} replace />
          )
        }
      />
      <Route
        path="/admin/home"
        element={
          user?.role === "admin" ? (
            <Navigate to={getTenantAdminPath("/dashboard")} replace />
          ) : (
            <Navigate to={getTenantAdminPath("/login")} replace />
          )
        }
      />


      {/* Fallback */}
      <Route path="*" element={<Navigate to={user ? getTenantScopedPath("/dashboard") : getTenantHomePath()} replace />} />
    </Routes>
          </Suspense>
        </ThemeProvider>
      </SiteNameProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
